import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { isMobileOnly, isIOS } from 'react-device-detect';
import { formatProduct } from 'utils/social-ecomm';

import copyToClipboard from 'utils/copyToClipboard';
import Button from 'shared-components/Button/Button';
import styles from './InviteLink.module.css';

const getTextMessage = (product) =>
  product
    ? `Check out this ${formatProduct(
        product
      )} I think you’d love!\n\nJoin me in shopping items at up to 70% off in a FabFitFun exclusive sale, plus get $10 credit when we both make a purchase.`
    : 'Shop with me, earn $10 credit when we both make a purchase, and discover items at up to 70% off in a FabFitFun exclusive sale!';

const sendSMS = (link, product) => {
  const textMessage = getTextMessage(product);
  const paramSeperator = isIOS ? '&' : '?';
  const sms = `sms:${paramSeperator}body=${encodeURIComponent(
    `${textMessage}\n\n${link}`
  )}`;
  window.location = sms;
};

const InviteLink = ({
  link,
  product,
  socialMediaCopy,
  facebookLoaded,
  onClickEmail,
  onClickShare,
  onInviteSent,
  trackInviteSent,
}) => {
  const [copied, setCopied] = useState(false);
  const buttonText = copied ? 'Copied' : 'Copy Link';
  const copyLink = () => {
    copyToClipboard(link);
    setCopied(true);
    trackInviteSent('COPY LINK');
    setTimeout(() => {
      setCopied(false);
    }, 5000); // wait 5 seconds before reenabling copy button
  };
  const handleClickText = () => {
    trackInviteSent('TEXT INVITE');
    sendSMS(link, product);
    onInviteSent(false);
  };
  const handleClickTweet = () => {
    trackInviteSent('TWITTER SHARE');
    window.location.href = `https://twitter.com/intent/tweet?url=${link}&text=${encodeURIComponent(
      socialMediaCopy
    )}`;
  };

  return (
    <div className={styles.container}>
      <form className={styles.inviteForm}>
        <input className={styles.inviteCode} value={link} readOnly />
        <Button
          className={styles.copyLink}
          onClick={copyLink}
          disabled={copied}
        >
          {buttonText}
        </Button>
      </form>
      <div className={styles.buttons}>
        {isMobileOnly ? (
          <button
            type="button"
            className={styles.shareButton}
            onClick={handleClickText}
          >
            <i className="fas fa-mobile-alt" />
            Text
          </button>
        ) : (
          <button
            type="button"
            className={styles.shareButton}
            onClick={onClickEmail}
          >
            <i className="far fa-envelope" />
            <span>Email</span>
          </button>
        )}
        {facebookLoaded && (
          <button
            type="button"
            className={styles.shareButton}
            onClick={onClickShare}
          >
            <i className="fab fa-facebook-square" />
            <span>Share</span>
          </button>
        )}
        <button
          type="button"
          className={classNames(
            'twitter-share-button',
            styles.shareButton,
            styles.twitterButton
          )}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleClickTweet}
        >
          <i className="fab fa-twitter" />
          Tweet
        </button>
      </div>
    </div>
  );
};

InviteLink.propTypes = {
  link: PropTypes.string.isRequired,
  product: PropTypes.shape({
    productId: PropTypes.string,
    productName: PropTypes.string,
    retailPrice: PropTypes.string,
    salePrice: PropTypes.string,
    sku: PropTypes.string,
  }),
  socialMediaCopy: PropTypes.string.isRequired,
  facebookLoaded: PropTypes.bool.isRequired,
  onClickEmail: PropTypes.func.isRequired,
  onClickShare: PropTypes.func.isRequired,
  onInviteSent: PropTypes.func.isRequired,
  trackInviteSent: PropTypes.func.isRequired,
};

InviteLink.defaultProps = {
  product: null,
};

export default InviteLink;
